import { useCallback, useContext, useEffect, useRef } from 'react'

import { useGlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { Drawing, DrawingParameterTextAnnotation, DrawingType } from '@editor/interfaces/drawing'
import { InspectionItem } from '@editor/interfaces/inspection'
import { updateInspectionItems } from '@editor/pages/projects/editor/store/inspectionItems'
import { getDrawingsOfType, isDrawingOfType } from '@editor/services/Drawing'
import { COMMON_MESSAGE } from '@editor/services/ErrorHandler'
import { patchInspectionItems } from '@editor/services/InspectionSheet'
import { withPromiseContained } from '@editor/services/Util'
import { GenericError } from '@editor/services/errors/GenericError'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { selectInspectionItems } from '@editor/store/selectors'
import { setSelectedElementIds } from '@editor/store/tool'
import * as Sentry from '@sentry/react'
import { cloneDeep } from 'lodash'
import { useDebouncedCallback } from 'use-debounce'

import { DEFAULT_DOCUMENT } from '../constants'
import { BlueprintEditorTools } from '../definitions'
import { setIsBatchSaving, setSelectedTool, updateBlueprint } from '../store/blueprintEditor'
import { setDrawings, setLastModified } from '../store/drawings'
import { Tools } from '../tools/setup'

const useDrawings = () => {
  // Contexts
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useGlobalModalContext()

  // Store
  const dispatch = useAppDispatch()
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const inspectionSheets = useAppSelector((state) => state.inspectionItems.inspectionSheets)
  const inspectionItems = useAppSelector(selectInspectionItems)
  const inspectionItemsLoading = useAppSelector((state) => state.inspectionItems.isLoading)

  const selectedBlueprint = useAppSelector((state) => state.blueprint.editor.selectedBlueprint)
  const previousSelectedBlueprintId = useAppSelector((state) => state.blueprint.editor.previousSelectedBlueprintId)
  const selectedTool = useAppSelector((state) => state.blueprint.editor.selectedTool)
  const document = useAppSelector((state) => state.blueprint.drawings.document)
  const lastModified = useAppSelector((state) => state.blueprint.drawings.lastModified)
  const isCreatingBlueprint = useAppSelector((state) => state.blueprint.editor.isCreatingBlueprint)
  const mode = useAppSelector((state) => state.blueprint.editor.mode)
  const isAllowedToModify = useAppSelector((state) => state.blueprint.editor.isAllowedToModify)

  // Refs
  const isSavingRef = useRef(false)
  const initializedBlueprintIdRef = useRef<string | null>(null)
  const lastModifiedRef = useRef(lastModified)
  const lastSavedDrawingsRef = useRef<Drawing[]>(document.drawings)
  const latestDocumentRef = useRef(document)

  // Derived state
  const currentEditorTool = Tools.find((tool) => tool.key === selectedTool)

  /**
   * Reset all drawing-related states.
   */
  const resetBlueprintDrawings = useCallback(() => {
    initializedBlueprintIdRef.current = null
    lastModifiedRef.current = 0
    dispatch(setLastModified(0))
    dispatch(setSelectedTool(BlueprintEditorTools.Selector))
  }, [dispatch])

  useEffect(() => {
    if (isCreatingBlueprint) {
      initializedBlueprintIdRef.current = null
    }
  }, [isCreatingBlueprint])

  /**
   * On load of blueprints, load drawings.
   * This should be done only once when the blueprint is selected.
   */
  useEffect(() => {
    // If there's no selected blueprint, or in creation mode (no sort defined yet), do not proceed.
    // If the blueprint is already initialized, do not re-initialize.
    if (
      !selectedBlueprint?.sort_key ||
      (initializedBlueprintIdRef.current && selectedBlueprint.blueprint_id === initializedBlueprintIdRef.current) ||
      inspectionItemsLoading
    )
      return

    if (previousSelectedBlueprintId && selectedBlueprint.blueprint_id !== previousSelectedBlueprintId) {
      resetBlueprintDrawings()
    }

    if (!initializedBlueprintIdRef.current) {
      const updatedDrawings = cloneDeep(selectedBlueprint.drawings || [])

      // go through inspection items to update linked annotation texts
      const inspectionItemMap = inspectionItems.items.reduce<Record<string, InspectionItem>>((acc, item) => {
        acc[item.inspection_item_id!] = item
        return acc
      }, {})

      updatedDrawings.forEach((drawing) => {
        if (
          isDrawingOfType<DrawingParameterTextAnnotation>(drawing, DrawingType.TextAnnotation) &&
          drawing.drawing_parameters.leadAnchors.length > 0
        ) {
          drawing.drawing_parameters.leadAnchors.forEach((anchor) => {
            if (anchor.inspectionItemId) {
              drawing.drawing_parameters.topText = inspectionItemMap[anchor.inspectionItemId]?.part_name
            }
          })
        }
      })

      dispatch(setDrawings(updatedDrawings))
      initializedBlueprintIdRef.current = selectedBlueprint.blueprint_id
      lastSavedDrawingsRef.current = cloneDeep(updatedDrawings)
    }
  }, [
    selectedBlueprint,
    previousSelectedBlueprintId,
    inspectionItems,
    inspectionItemsLoading,
    resetBlueprintDrawings,
    dispatch,
  ])

  /**
   * When user changes tool to something other than Selector,
   * clear the selected drawing.
   */
  useEffect(() => {
    if (selectedTool !== BlueprintEditorTools.Selector) {
      dispatch(setSelectedElementIds([]))
    }
  }, [selectedTool, dispatch])

  /**
   * Check if the current tool is able to be used for the current mode.
   * If not, switch to Selector tool.
   */
  useEffect(() => {
    if (currentEditorTool?.toolbar?.modes && !currentEditorTool.toolbar.modes.includes(mode)) {
      dispatch(setSelectedTool(BlueprintEditorTools.Selector))
    }
  }, [currentEditorTool, mode, dispatch])

  /**
   * Save inspection item names that have been modified
   * through linked annotations.
   */
  const saveInspectionItemNames = useCallback(async () => {
    if (!project || !inspectionSheets || !lastSavedDrawingsRef.current) return

    // -- Diff new data with old data to find modified items.
    const changedItems = getDrawingsOfType<DrawingParameterTextAnnotation>(
      latestDocumentRef.current.drawings,
      DrawingType.TextAnnotation,
    ).reduce<Partial<InspectionItem>[]>((acc, { drawing_parameters: { uuid, topText, leadAnchors } }) => {
      // Don't proceed if no linked inspection items.
      const linkedAnchors = leadAnchors.filter((anchor) => anchor.inspectionItemId)
      if (linkedAnchors.length === 0 || !linkedAnchors[0].inspectionItemId) return acc

      // It's a new annotation, so there's nothing to update.
      // Or nothing has changed.
      const oldDrawing = lastSavedDrawingsRef.current?.find((d) => d.drawing_parameters.uuid === uuid)

      // Need the inspection item itself for some more data.
      const item = inspectionItems.getById(linkedAnchors[0].inspectionItemId)

      if (
        !item?.inspection_area_id ||
        (!oldDrawing && item.isPartNameDefaulted) ||
        (oldDrawing &&
          (!isDrawingOfType(oldDrawing, DrawingType.TextAnnotation) ||
            (oldDrawing.drawing_parameters as DrawingParameterTextAnnotation).topText === topText)) ||
        !inspectionSheets[item.inspection_area_id].inspection_sheet_id
      )
        return acc

      acc.push({
        inspection_area_id: item.inspection_area_id,
        inspection_sheet_id: inspectionSheets[item.inspection_area_id].inspection_sheet_id,
        inspection_item_id: linkedAnchors[0].inspectionItemId,
        part_name: topText || '',
      })

      return acc
    }, [])

    // -- Update inspection item names
    const accessToken = await getAccessToken()
    if (!accessToken) return

    const result = await patchInspectionItems(accessToken, project.project_id, changedItems, showErrorModal)
    if (!result) {
      return
    }

    dispatch(updateInspectionItems(result))
  }, [
    project,
    inspectionItems,
    inspectionSheets,
    latestDocumentRef,
    lastSavedDrawingsRef,
    showErrorModal,
    getAccessToken,
    dispatch,
  ])

  /**
   * Handle saving of drawings.
   */
  const handleSave = useDebouncedCallback(async (modified: number) => {
    // Only 1 can run at a time.
    if (!project || !inspectionArea || !selectedBlueprint?.sort_key || isSavingRef.current) return

    isSavingRef.current = true
    dispatch(setIsBatchSaving(true))

    const runSave = async () =>
      withPromiseContained(async () => {
        const accessToken = await getAccessToken()
        if (!accessToken) return

        // Create or update drawings
        await Promise.all([
          saveInspectionItemNames(),
          dispatch(
            updateBlueprint({
              accessToken,
              projectId: project.project_id,
              areaId: inspectionArea.inspection_area_id,
              blueprint: {
                ...selectedBlueprint,
                drawings: latestDocumentRef.current.drawings,
                inspection_items: latestDocumentRef.current.editorElements.inspectionItems,
                polygons: latestDocumentRef.current.editorElements.polygons,
                cylinders: latestDocumentRef.current.editorElements.cylinders,
              },
            }),
          ).unwrap(),
        ])

        lastSavedDrawingsRef.current = cloneDeep(latestDocumentRef.current.drawings)
      })

    await runSave()
      .finally(async () => {
        if (lastModifiedRef.current > modified) {
          await runSave()
        }

        isSavingRef.current = false
        dispatch(setIsBatchSaving(false))
      })
      .catch((err: Error) => {
        // TODO: Handle error properly by placing data in localStorage and retrying later.
        showErrorModal(
          err instanceof GenericError || (err instanceof Object && 'name' in err && err.name === 'GenericError')
            ? err.message
            : COMMON_MESSAGE,
        )
        Sentry.captureException(err)
      })
  }, 3000)

  /**
   * Request for a saving operation.
   * This _takes_ requests of items to be saved, but does not necessarily save them immediately.
   * If there's a request already in progress, it will queue the next request.
   * This is to make sure no conflicting requests are made if the earlier request is still being processed.
   */
  useEffect(() => {
    if (!lastModified || !isAllowedToModify) return

    lastModifiedRef.current = lastModified
    latestDocumentRef.current = document

    // Ignore if it's already saving to prevent duplicate saves as queue will auto-execute the next save.
    //   └─> this can happen if user make changes while save is in progress which queue will execute as the save
    //       completes, but the debounce will trigger too with the same timestamp later if no further changes are made.
    if (!isSavingRef.current) {
      void handleSave(lastModified)
    }
  }, [handleSave, document, lastModified, isAllowedToModify])

  /**
   * Clean up on unmount.
   * Clears all refs and states related to drawings.
   */
  useEffect(
    () => () => {
      initializedBlueprintIdRef.current = null
      lastModifiedRef.current = 0
      latestDocumentRef.current = cloneDeep(DEFAULT_DOCUMENT)
      lastSavedDrawingsRef.current = cloneDeep(DEFAULT_DOCUMENT.drawings)
      dispatch(setDrawings([]))
      dispatch(setLastModified(0))
    },
    [dispatch],
  )

  return {
    resetBlueprintDrawings,
  }
}

export default useDrawings
